import React, { useEffect, useContext, useReducer } from "react";
import { userType, userReducer } from "src/reducers/userReducer";
import { useLocation } from "react-router-dom";
import * as API from "src/api/request";
import { notification } from "antd";
import Swal from "sweetalert2";
import _ from "lodash";

/**
 * НЭВТРЭЛТ ШААРДАХГҮЙ замууд.
 *
 * ⚠ Энэ жагсаалт МАШ БОГИНО байх ёстой. Зам нэмэх бүрдээ «нэвтрээгүй хүн
 *   энэ хуудсанд юу харах вэ» гэдгийг backend талаас нь ЗААВАЛ хязгаарлана
 *   — фронтенд нь хамгаалалт биш, зөвхөн харагдац.
 *
 * · `/assessment…` — өмнөх байдал хэвээр
 * · `/knowledge/qr/<КОД>` — тоног төхөөрөмж дээрх наалт. Ажилтан талбар
 *   дээр зогсоод ямар ХАБ заавар мөрдөхөө шууд харна. Backend нь
 *   `/xab/knowledge/qr/public/…`-аар ЗӨВХӨН гарчиг буцаана.
 *   ⚠ `/knowledge/qr-admin` нь ӨӨР зам — нэвтрэлттэй хэвээр.
 */
export function isPublicPath(pathname) {
  const seg = String(pathname || "").split("/");
  if (seg[1] === "assessment") return true;
  if (seg[1] === "knowledge" && seg[2] === "qr" && seg[3]) return true;
  return false;
}

const _user = {
  tn: 0,
  info: {},
  userdeps: [],
  usergroup: "",
  userGroupList: [],
  usermenu: [],
  userzone: [],
  loggedIn: false,
  current: {
    menu1: 1900,
    menu2: "",
    menuExpanded: "",
    menuName: "",
  },
  template: {
    theme: "",
    header: "",
    sidebar: "leftmenu-light",
    showSidebar: false,
  },
};

const context = React.createContext();

export const useUserContext = () => {
  const ctx = useContext(context);
  if (ctx === undefined) {
    throw new Error("useAuthState must be used within a AuthProvider");
  }
  return ctx;
};

const UserContext = ({ children }) => {
  const location = useLocation();
  const [state, dispatch] = useReducer(userReducer, _user);

  const [api, contextHolder] = notification.useNotification();

  // sso_owner = програмын админ — эрхийн хязгаарлалтгүй БҮГДИЙГ харна.
  // Role/group-оор эрх шалгадаг бүх газар админ нь бүх эрхтэй гэж тооцогдоно
  // (checkRole ба checkGroup хоёул үргэлж true). Хэрэв ямар нэг хуудас админыг
  // ТУСГАЙЛАН салгах шаардлагатай бол `isAdmin`-ийг ашиглаж, эсвэл raw
  // `user.userroles`-оор шалгана (ж: incident/register-ийн зөрчил routing).
  const ADMIN_ROLE = "sso_owner";
  // sso_owner нь эх үүсвэрээс хамаараад `userroles` (Redis) эсвэл `roles` (SSO
  // токен) талбарын алинд нь ч ирж болно — хоёуланг нь шалгаж админыг найдвартай
  // илрүүлнэ (эс бөгөөс checkRole/checkGroup bypass зарим хуудсанд ажиллахгүй).
  const isAdmin = () =>
    _.includes(state.userroles, ADMIN_ROLE) ||
    _.includes(state.roles, ADMIN_ROLE);

  const checkGroup = (value) =>
    isAdmin() || _.some(state.userGroupList, (item) => value.includes(item));
  const checkRole = (value) =>
    isAdmin() || _.some(state.userroles, (item) => value.includes(item));

  const message = ({ type, error = null, title, description = null }) => {
    if (type === "error") {
      var message = error?.response?.data?.message
        ? error?.response?.data?.message
        : title;
      var desc = null;
      if (!message) {
        desc = error?.response?.data?.message
          ? error?.response?.data?.message
          : error.toJSON().message;
      }
      api.error({
        message: message,
        description: desc,
        placement: "topRight",
        duration: 5,
      });
    }
    if (type === "info") {
      api.info({
        message: title,
        description: description,
        placement: "topRight",
        duration: 5,
      });
    }
    if (type === "success") {
      api.success({
        message: title,
        description: description,
        placement: "topRight",
        duration: 5,
      });
    }
    if (type === "warning") {
      api.warning({
        message: title,
        description: description,
        placement: "topRight",
        duration: 5,
      });
    }
  };

  useEffect(() => {
    if (navigator.onLine) {
      var token =
        typeof window !== "undefined" ? localStorage.getItem("token") : null;
      if (token === null) {
        if (!isPublicPath(location.pathname)) {
          // ⚠ Уншуулсан QR / нээсэн хуудсыг САНАНА. Урьд нь SSO-гийн дараа
          //   `/callback` дээр л үлдэж, хэрэглэгч нүүр хуудсанд очдог байв —
          //   ажилтан талбар дээр наалт уншуулаад нэвтэрсний дараа юу үзэх
          //   гэж байснаа алддаг байсан.
          try {
            sessionStorage.setItem(
              "post_login_path",
              location.pathname + (location.search || ""),
            );
          } catch (e) {
            /* хувийн горимд sessionStorage хаалттай байж болно */
          }
          API.getAuth().then((res) => {
            const result = res.data;
            const originalUrl = result.url;
            const newRedirectUri = `${window.location.origin}/callback`;
            const url = new URL(originalUrl);
            url.searchParams.set("redirect_uri", newRedirectUri);
            const newUrl = url.href;
            window.location.replace(newUrl);
          });
        }
      } else {
        // ⚠ ЭНД `isPublicPath` ШАЛГАХГҮЙ. Токентой хэрэглэгчийн мэдээллийг
        //   ҮРГЭЛЖ татна — эс бөгөөс нэвтэрсэн хүн QR уншуулахад
        //   `loggedIn` тогтохгүй, Layout-гүй «нийтийн» харагдац руу унана.
        //   Нийтийн зам нь зөвхөн SSO рүү ЧИГЛҮҮЛЭХЭЭС хамгаална.
        if (location.pathname.split("/")[1] !== "assessment") {
          if (state.tn === 0) {
            API.getUserInfo().then((userInfo) => {
              dispatch({
                type: userType.LOG_IN,
                data: userInfo,
              });
            });
          }
        }
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "Интернэт холболтоо шалгана уу.",
        html: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tn]);

  useEffect(() => {
    var menu1ID = localStorage.getItem("menu1ID");
    dispatch({
      type: userType.CHANGE_MENU1_ID,
      data: menu1ID === null ? 1900 : menu1ID,
    });

    var menu2ID = localStorage.getItem("menu2ID");
    dispatch({
      type: userType.CHANGE_MENU2_ID,
      data: menu2ID === null ? 0 : menu2ID,
    });

    var menuExpanded = localStorage.getItem("menuExpanded");
    dispatch({
      type: userType.CHANGE_MENU_EXPANDED,
      data: menuExpanded === null ? 0 : menuExpanded,
    });

    var theme = localStorage.getItem("theme");
    !theme || dispatch({ type: userType.CHANGE_THEME, data: theme });

    var header = localStorage.getItem("header");
    !header || dispatch({ type: userType.CHANGE_HEADER, data: header });

    var sidebar = localStorage.getItem("sidebar");
    !sidebar || dispatch({ type: userType.CHANGE_SIDEBAR, data: sidebar });
  }, []);

  return (
    <context.Provider
      value={{
        user: state,
        userDispatch: dispatch,
        userType: userType,
        message,
        checkGroup,
        checkRole,
        isAdmin,
      }}
    >
      {contextHolder}
      {children}
    </context.Provider>
  );
};

export default React.memo(UserContext);
